import React, { useContext } from "react";
import {
  // _LandingFooterCtaWrapper,
  _LandingFooterRoot,
} from "./LandingFooter.styled";
import { ImageResponsive, ImageUrlContext } from "../../Image";
import { ImageFit, ImageFormat } from "../../Image/interface/ImageData";
import Typography from "../../../Foundation/Typography/Typography";
import FlexBox from "../../FlexBox/FlexBox";
import Link from "../../../Core/Link/Link";
import LinkContext from "../../../Core/Link/Context/LinkContext";
import { IconName } from "design-system/src/lib/svgIcon/components";
import { useHighlightedTypographyContext } from "../../HighlightedTypography/HighlightedTypography.context";
import htmlParse from "html-react-parser";
import { isEmptyImageId } from "../../Image/Image.utils";
import type { ILogoData } from "../LandingHeader/LandingHeader";
import useLogoSvg from "../LandingHeader/useLogoSvg";

export interface ILandingFooterProps extends ILogoData {
  logoLink?: string;
  title: string;
  hashtag?: string;
  socialNetworkLabel?: string;

  onClick?: () => void;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    pinterest?: string;
  };

  footerText?: string;

  backgroundImage?: {
    desktop?: string;
    mobile?: string;
  };
}
type SocialKey = keyof NonNullable<ILandingFooterProps["socialLinks"]>;
export const socialIcon: Record<SocialKey, IconName> = {
  facebook: "logo_facebook",
  instagram: "logo_instagram",
  tiktok: "logo_tiktok",
  pinterest: "logo_pinterest",
};
export const SOCIAL_KEYS_ORDER: SocialKey[] = [
  "facebook",
  "instagram",
  "tiktok",
  "pinterest",
];

const LandingFooter = (props: ILandingFooterProps) => {
  const {
    logoId,
    logoLink,
    title,
    hashtag,
    socialNetworkLabel,
    socialLinks,
    footerText,
    backgroundImage,
    logoSource = "image",
    svgLogo,
    lang,
  } = props;
  const { StyledLink } = useContext(LinkContext);
  const { generateImageUrl } = useContext(ImageUrlContext);
  const { highlightedColor: highlightedColorContext } =
    useHighlightedTypographyContext();
  const { logoSvgSrc } = useLogoSvg(svgLogo, lang);

  return (
    <_LandingFooterRoot
      $bg={{
        desktop:
          (backgroundImage &&
            backgroundImage.desktop &&
            !isEmptyImageId(backgroundImage.desktop) &&
            generateImageUrl({
              id: backgroundImage?.desktop || "-1",
              width: "auto",
              height: "auto",
              quality: 100,
              format: ImageFormat.WEBP,
            })) ||
          undefined,
        mobile:
          (backgroundImage &&
            backgroundImage.mobile &&
            !isEmptyImageId(backgroundImage.mobile) &&
            generateImageUrl({
              id: backgroundImage?.mobile || "-1",
              width: typeof window !== "undefined" ? window.innerWidth : "auto",
              height: "auto",
              quality: 90,
              format: ImageFormat.WEBP,
            })) ||
          undefined,
      }}
    >
      <FlexBox direction="column" justify="center" align="center">
        {logoSource === "image"
          ? logoId &&
            !isEmptyImageId(logoId) && (
              <StyledLink
                to={logoLink || "#"}
                className="logo-link"
                $hover={false}
                $color={""}
              >
                <ImageResponsive
                  alt=""
                  desktop={{
                    image: {
                      id: logoId,
                      fit: ImageFit.COVER,
                      quality: 80,
                      aspectRatio: 2 / 1,
                      width: 280,
                    },
                    imgStyle: {
                      overflow: "hidden",
                      aspectRatio: 2 / 1,
                      width: "100%",
                      // maxWidth: 280,
                    },
                  }}
                  mobile={{
                    image: {
                      id: logoId,
                      fit: ImageFit.COVER,
                      quality: 80,
                      aspectRatio: 2 / 1,
                      outputPixelDensities: [1, 2],
                      width: 210,
                    },
                    imgStyle: {
                      overflow: "hidden",
                      aspectRatio: 2 / 1,
                      width: "100%",
                    },
                  }}
                />
              </StyledLink>
            )
          : svgLogo &&
            logoSvgSrc && (
              <StyledLink
                to={logoLink || "#"}
                className="logo-link"
                $hover={false}
                $color={""}
              >
                <img
                  src={logoSvgSrc}
                  alt=""
                  style={{ maxWidth: 280, width: "100%" }}
                />
              </StyledLink>
            )}
        <Typography variant="h7" className="landing-footer-title">
          {title}
        </Typography>
        <Typography
          variant="h4"
          className="landing-footer-hashtag"
          color={() => highlightedColorContext + ""}
        >
          {hashtag}
        </Typography>
        <FlexBox direction="column" justify="center" align="center" gap={24}>
          <Typography variant="ui" className="landing-footer-social-label">
            {socialNetworkLabel}
          </Typography>
          {socialLinks && (
            <FlexBox gap={16}>
              {SOCIAL_KEYS_ORDER.map((key) => {
                const value = socialLinks[key];
                if (!value) return null;
                const iconName = socialIcon[key];
                return (
                  <Link
                    key={key}
                    href={value}
                    url={value}
                    to={value}
                    icon={iconName}
                    target="_blank"
                    label=""
                    color={() => highlightedColorContext || "black"}
                  />
                );
              })}
            </FlexBox>
          )}
        </FlexBox>

        <Typography variant="ui3" className="landing-footer-bottom-text">
          {htmlParse(footerText + "")}
        </Typography>
      </FlexBox>
    </_LandingFooterRoot>
  );
};

export default LandingFooter;
